import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { TwilioConnection } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Field } from "@/components/ui/Field";
import { Button } from "@/components/ui/Button";
import { ArrowLeftIcon, CheckIcon, XIcon } from "@/lib/icons";

type State =
  | { status: "loading" }
  | { status: "ready"; conn: TwilioConnection }
  | { status: "error"; message: string };

export function RotateKeyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: "loading" });
  const [apiKeySid, setApiKeySid] = useState("");
  const [apiKeySecret, setApiKeySecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Look up the connection so we can show the user which one they're rotating.
  // We only have the list endpoint right now — fine for a small number of
  // connections per tenant. When the list grows, add GET /:id.
  useEffect(() => {
    if (!id) return;
    api.twilio
      .list()
      .then((r) => {
        const c = r.connections.find((x) => x.id === id);
        if (!c) {
          setState({ status: "error", message: "Connection not found" });
        } else {
          setState({ status: "ready", conn: c });
        }
      })
      .catch((e) =>
        setState({
          status: "error",
          message: e instanceof Error ? e.message : "Failed to load",
        }),
      );
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.twilio.update(id, { apiKeySid, apiKeySecret });
      navigate("/connections");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Failed to rotate key");
    } finally {
      setBusy(false);
    }
  }

  if (state.status === "loading") {
    return <p className="text-sm text-zinc-500 dark:text-zinc-500">Loading…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="space-y-3">
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
        <Link
          to="/connections"
          className="inline-flex items-center gap-1.5 text-[13px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          <ArrowLeftIcon size={14} />
          Back to connections
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Manage connection
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Rotate API Key
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Replace the stored credentials for{" "}
            <strong className="font-semibold text-zinc-800 dark:text-zinc-200">
              {state.conn.friendlyName ?? state.conn.accountSid}
            </strong>
            . The Account SID stays the same.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-zinc-200 bg-white p-6 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-card dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-400">
            Account SID:{" "}
            <span className="font-mono text-zinc-800 dark:text-zinc-300">
              {state.conn.accountSid}
            </span>
          </div>

          <Field
            label="New API Key SID"
            required
            placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            className="font-mono tracking-tight"
            autoComplete="off"
            value={apiKeySid}
            onChange={(e) => setApiKeySid(e.target.value)}
          />
          <Field
            label="New API Key Secret"
            type="password"
            required
            placeholder="••••••••••••••••••••••••••••••••"
            className="font-mono tracking-tight"
            autoComplete="off"
            value={apiKeySecret}
            onChange={(e) => setApiKeySecret(e.target.value)}
          />

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" type="button" onClick={() => navigate("/connections")}>
              <XIcon size={14} />
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              <CheckIcon size={14} />
              {busy ? "Rotating…" : "Save new key"}
            </Button>
          </div>
        </form>
      </div>

      <aside className="space-y-3">
        <InfoCard
          title="Why rotate?"
          description="Rotate periodically (every 90 days is a healthy default) or immediately if you suspect a key has leaked. The old key keeps working in Twilio until you revoke it there."
        />
        <InfoCard
          title="No downtime"
          description="We replace the stored credential atomically. In-flight requests use the old key; the next request uses the new one."
        />
        <InfoCard
          title="Revoke the old key in Twilio"
          description="After saving here, delete the previous API Key from Twilio Console → Account → API keys to make the rotation real."
        />
      </aside>
    </div>
  );
}

function InfoCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/30 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <div className="text-[12.5px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</div>
      <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-600 dark:text-zinc-400">
        {description}
      </p>
    </div>
  );
}
